import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const DataContext = createContext(null);
const POLL_INTERVAL_MS = 20000; // 20s background refresh

export function DataProvider({ children }) {
  const { user, impersonation } = useAuth();
  const [calls, setCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [callsFetchedOnce, setCallsFetchedOnce] = useState(false);
  const [leadsFetchedOnce, setLeadsFetchedOnce] = useState(false);
  const inflightCalls = useRef(false);
  const inflightLeads = useRef(false);

  // Reset cache whenever user/impersonation identity flips (login, logout, switch workspace)
  const identityKey = `${user?.id || ""}|${impersonation?.workspace_id || ""}`;
  useEffect(() => {
    setCalls([]);
    setLeads([]);
    setCallsFetchedOnce(false);
    setLeadsFetchedOnce(false);
  }, [identityKey]);

  const refreshCalls = useCallback(async ({ silent = false } = {}) => {
    if (!user || user.role === "admin" && !impersonation) return;
    if (inflightCalls.current) return;
    inflightCalls.current = true;
    if (!silent) setCallsLoading(true);
    try {
      const { data } = await api.get("/vapi/calls");
      setCalls(Array.isArray(data) ? data : []);
      setCallsFetchedOnce(true);
    } catch (_) {
      /* keep stale cache on error */
    } finally {
      inflightCalls.current = false;
      if (!silent) setCallsLoading(false);
    }
  }, [user, impersonation]);

  const refreshLeads = useCallback(async ({ silent = false } = {}) => {
    if (!user || user.role === "admin" && !impersonation) return;
    if (inflightLeads.current) return;
    inflightLeads.current = true;
    if (!silent) setLeadsLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(Array.isArray(data) ? data : []);
      setLeadsFetchedOnce(true);
    } catch (_) { /* keep cache */ }
    finally {
      inflightLeads.current = false;
      if (!silent) setLeadsLoading(false);
    }
  }, [user, impersonation]);

  const refreshAll = useCallback((opts) => {
    refreshCalls(opts);
    refreshLeads(opts);
  }, [refreshCalls, refreshLeads]);

  // Initial fetch + background polling when the user has access to client data
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin" && !impersonation) return;
    refreshAll({ silent: false });
    const t = setInterval(() => refreshAll({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);

  const patchLead = useCallback((leadId, updated) => {
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...updated } : l)));
  }, []);

  const value = useMemo(() => ({
    calls,
    leads,
    callsLoading,
    leadsLoading,
    callsFetchedOnce,
    leadsFetchedOnce,
    refreshCalls,
    refreshLeads,
    refreshAll,
    patchLead,
  }), [calls, leads, callsLoading, leadsLoading, callsFetchedOnce, leadsFetchedOnce, refreshCalls, refreshLeads, refreshAll, patchLead]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export const useData = () => useContext(DataContext);
